import type { ClientPersonReference } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Portal } from 'react-native-paper';

import { useAddAdminStatus } from '@/features/api/hooks/grapevine-group-events/useAddAdminStatus';
import { useRemoveAdminStatus } from '@/features/api/hooks/grapevine-group-events/useRemoveAdminStatus';
import { useRemoveGroupMember } from '@/features/api/hooks/grapevine-group-events/useRemoveGroupMember';
import { Alert } from '@/features/popup/components/Alert';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { useAppNavigation } from '@/navigation/hooks/useAppNavigation';
import { AppRoute } from '@/navigation/types/navigation';
import type { ExtendedMenuProps } from '@/shared/components/ExtendedMenu';
import { ExtendedMenu } from '@/shared/components/ExtendedMenu';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  groupId: string;
  member: ClientPersonReference;
  userIsAdmin: boolean;
  userIsMember: boolean;
  memberIsAdmin: boolean;
} & Omit<ExtendedMenuProps, 'children'>;

export const GroupMemberMenu: FC<Props> = ({
  groupId,
  member,
  userIsAdmin,
  userIsMember,
  memberIsAdmin,
  onDismiss,
  ...menuProps
}) => {
  const { t } = useTranslation(LocalizationNameSpace.GROUPS, {
    keyPrefix: 'components.groupMemberMenu'
  });
  const theme = useAppTheme();
  const navigation = useAppNavigation();

  const [showRemoveAlert, setShowRemoveAlert] = useState<boolean>(false);

  const { mutate: addAdminStatus } = useAddAdminStatus(groupId);
  const { mutate: removeAdminStatus } = useRemoveAdminStatus(groupId);
  const { mutate: removeGroupMember, isPending } =
    useRemoveGroupMember(groupId);

  return (
    <>
      {showRemoveAlert && (
        <Portal>
          <Alert
            isVisible
            labels={t('alerts.remove', {
              returnObjects: true,
              name: `${member.firstName} ${member.lastName}`
            })}
            dismissable={!isPending}
            onClose={() => setShowRemoveAlert(false)}
            confirmColor={theme.colors.error}
            isConfirmLoading={isPending}
            dismissOnConfirm={false}
            onConfirm={() =>
              removeGroupMember(member.id, {
                onSuccess: () => setShowRemoveAlert(false)
              })
            }
          />
        </Portal>
      )}
      <ExtendedMenu onDismiss={onDismiss} {...menuProps}>
        {userIsMember && (
          <Menu.Item
            title={t('writePrivate')}
            onPress={() => {
              navigation.navigate(AppRoute.NewChatOnGroup, {
                groupId,
                memberId: member.id
              });
              onDismiss?.();
            }}
          />
        )}
        {
          // Todo: not implemented yet
          /*
        <Menu.Item
          title={t('showProfile')}
          onPress={() => {
            // Todo: member profile
            onDismiss?.();
          }}
        />
        */
        }
        {userIsAdmin && !memberIsAdmin && (
          <Menu.Item
            title={t('addAdminStatus')}
            onPress={() => {
              addAdminStatus(member.id);
              onDismiss?.();
            }}
          />
        )}
        {userIsAdmin && memberIsAdmin && (
          <Menu.Item
            title={t('removeAdminStatus')}
            onPress={() => {
              removeAdminStatus(member.id);
              onDismiss?.();
            }}
            theme={{ colors: { onSurface: theme.colors.error } }}
          />
        )}
        {userIsAdmin && (
          <Menu.Item
            title={t('removeFromGroup')}
            onPress={() => {
              setShowRemoveAlert(true);
              onDismiss?.();
            }}
            theme={{ colors: { onSurface: theme.colors.error } }}
          />
        )}
      </ExtendedMenu>
    </>
  );
};
